import { useContext, type FC, type SetStateAction } from 'react';
import SearchBlockContext, { type SearchBlockContextProps } from './SearchBlockContext';
import ListingContent from '3-components/ListingApp/ListingContent/ListingContent';
import { ListingLoader, ListingError, ListingNoItem } from '3-components/ListingApp/ListingUI/ListingUI';
import { searchResultsTypes, searchTypeMap, type SearchResponse, type SearchResult } from './SearchBlock';
import SearchItem from './SearchItem';
import { useQuery } from '@tanstack/react-query';
import SearchPagination from './SearchPagination';

interface SearchResultsProps {
    searchQuery: string;
    selectedSearchType: string | null;
    hasSearchQuery: boolean;
    setHasSearchQuery: React.Dispatch<React.SetStateAction<boolean>>;
    currentPage: number;
    setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
}

interface PayloadProps {
    count: boolean;
    facets: string[];
    filter?: string;
    search: string;
    queryType: string;
    searchMode: string;
    top?: number;
    skip?: number;
}

const fetchFromSearchAPI = async(url: string, payload: PayloadProps): Promise<SearchResponse> => {
    const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    });

    if (!response.ok) {
        throw new Error('Error fetching data from API');
    }

    return response.json();
};

const normalizeUrl = (url: string): string => {
    return url.startsWith('~/') ? url.replace('~/', '/') : url;
};

const fetchSearchResults = (searchQuery: string, apiUrl: string, selectedType: string | null = null, page = 1, perPage = 10): Promise<SearchResponse> => {
    if (!searchQuery) {
        return Promise.resolve({ value: [], '@odata.count': 0 });
    }

    const payload = {
        count: true,
        facets: ['SearchPageType'],
        filter: selectedType ? `SearchPageType eq '${selectedType}'` : searchResultsTypes.map(type => {
            return `SearchPageType eq '${type}'`;
        }).join(' or '),
        search: searchQuery,
        queryType: 'full',
        searchMode: 'any',
        top: page === 1 ? 10 : perPage,
        skip: page === 1 ? 0 : (page - 1) * perPage,
    };

    return fetchFromSearchAPI(apiUrl, payload);
};

const SearchResults: FC<SearchResultsProps> = ({ searchQuery, selectedSearchType, currentPage, setCurrentPage, hasSearchQuery, setHasSearchQuery }) => {
    const { label, searchResultsPerPage = 10, apiUrl } = useContext(SearchBlockContext) as SearchBlockContextProps;

    const { data, error, isLoading, isFetching } = useQuery(
        ['searchResults', searchQuery, selectedSearchType ?? '', currentPage],
        () => {
            return fetchSearchResults(searchQuery, apiUrl, selectedSearchType, currentPage, searchResultsPerPage);
        },
        {
            enabled: !!searchQuery,
            staleTime: 1000 * 60 * 5,
            cacheTime: 0,
            onSuccess: (responseData: SearchResponse) => {
                if (!hasSearchQuery) {
                    // Update searchTypeMap based on facets
                    const searchPageTypeFacet = responseData['@search.facets']?.SearchPageType;
                    if (Array.isArray(searchPageTypeFacet)) {
                        searchPageTypeFacet.forEach((facet: { count: number; value: string }) => {
                            searchTypeMap.set(facet.value, facet.count);
                        });
                    }
                    setHasSearchQuery(true);
                }
            },
        }
    );

    const totalItems = data ? data['@odata.count'] : 0;

    if (isLoading) {
        return <ListingLoader loadingMessage={label.loadingMessage} />;
    }

    if (error) {
        return <ListingError errorMessage={label.errorMessage} />;
    }

    if (!data?.value || !data.value.length) {
        return <ListingNoItem noItemMessage={label.noItemMessage} />;
    }

    const handlePageChange = (pageNumber: SetStateAction<number>): void => {
        setCurrentPage(pageNumber);
    };

    return (
        <ListingContent
            isFetching={isFetching}
            isNoItems={!data?.value.length}
            loadingLabel={label.loadingMessage}
            noItemsLabel={label.noItemMessage}
            cols="1"
        >
            <div className="search-block-search-result">
                {data?.value.map((props: SearchResult) => {
                    const {
                        Title,
                        Description,
                        Image,
                        Url,
                        Label,
                        SearchPageType,
                        ActivityLocation,
                        ActivityStartDate,
                        ActivityEndDate,
                        ArticlePublishDate,
                        DocumentFileType,
                        DocumentFileSize,
                        DocumentFileViewURL,
                        DocumentFileDownloadURL,
                    } = props;

                    return (
                        <SearchItem
                            key={Url}
                            img={Image}
                            link={normalizeUrl(Url)}
                            title={Title}
                            type={SearchPageType}
                            tags={Label}
                            eventLocation={ActivityLocation}
                            eventStartDate={ActivityStartDate}
                            eventEndDate={ActivityEndDate}
                            newsPublishDate={ArticlePublishDate}
                            fileType={DocumentFileType}
                            fileSize={DocumentFileSize}
                            documentFileViewURL={normalizeUrl(DocumentFileViewURL ?? '')}
                            documentFileDownloadURL={normalizeUrl(DocumentFileDownloadURL ?? '')}
                        >
                            {Description}
                        </SearchItem>
                    );
                })}
                {(totalItems ?? 0) > searchResultsPerPage && (
                    <SearchPagination
                        currentPage={currentPage}
                        totalItems={totalItems ?? 0}
                        itemsPerPage={searchResultsPerPage}
                        onPageChange={handlePageChange}
                    />
                )}
            </div>
        </ListingContent>
    );
};

export default SearchResults;
